import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import LinkOffIcon from "@mui/icons-material/LinkOff";

const EmployeeHierarchyTable = ({
  hierarchyList,
  loader,
  page,
  rowsPerPage,
  total,
  setPage,
  setRowsPerPage,
  handleRemove,
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const data = hierarchyList || [];

  // API returns supervisor_name / subordinate_name directly
  const nameOf = (row, prefix) =>
    row[`${prefix}_name`] || row[`${prefix}_mobile`] || "-";

  if (loader) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: 300, alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
        Hierarchy List
      </Typography>

      {/* Desktop View */}
      {!isMobile ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1976d2" }}>
                {["S.No", "Supervisor", "Subordinate", "Action"].map((h) => (
                  <TableCell key={h} sx={{ color: "white", fontWeight: "bold" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data.length > 0 ? (
                data.map((row, index) => (
                  <TableRow key={`${row.supervisor_id}-${row.subordinate_id}`} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{nameOf(row, "supervisor")}</TableCell>
                    <TableCell>{nameOf(row, "subordinate")}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(row.supervisor_id, row.subordinate_id)}
                        sx={{ backgroundColor: "error.main", color: "white", "&:hover": { backgroundColor: "error.dark" } }}
                        title="Remove link"
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">No Data Found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Mobile View */
        <Box>
          {data.length > 0 ? (
            data.map((row, index) => (
              <Card
                key={`${row.supervisor_id}-${row.subordinate_id}`}
                sx={{ mb: 2, borderLeft: "5px solid #1976d2", boxShadow: 2 }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                        #{page * rowsPerPage + index + 1}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 0.5 }}>
                        Supervisor: {nameOf(row, "supervisor")}
                      </Typography>
                      <Typography variant="body2">
                        Subordinate: {nameOf(row, "subordinate")}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(row.supervisor_id, row.subordinate_id)}
                      sx={{ backgroundColor: "error.main", color: "white", "&:hover": { backgroundColor: "error.dark" } }}
                    >
                      <LinkOffIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : (
            <Typography align="center">No Data Found</Typography>
          )}
        </Box>
      )}

      {/* Pagination */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        sx={{ mt: 2 }}
      />
    </Box>
  );
};

export default EmployeeHierarchyTable;
